import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatIcon from '@mui/icons-material/Chat';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SchoolIcon from '@mui/icons-material/School';
import CommuteIcon from '@mui/icons-material/Commute';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import ArticleIcon from '@mui/icons-material/Article';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CategoryIcon from '@mui/icons-material/Category';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LanIcon from '@mui/icons-material/Lan';
import CoinsIcon from '@mui/icons-material/MonetizationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { APP_NAME } from '../config/constants';
import { AuthContext } from '../context/AuthContext';

const drawerWidth = 270;

const menuItems = [
  { label: 'Dashboard', icon: DashboardIcon, path: '/', permission: 'reports.revenue' },
  { label: 'Users', icon: PeopleIcon, path: '/users', permission: 'users.read' },
  { label: 'Staff', icon: BadgeIcon, path: '/staff', permission: 'users.read' },
  {
    label: 'Astrologers',
    icon: AutoAwesomeIcon,
    permission: 'astrologers.read',
    children: [
      { label: 'All Astrologers', path: '/astrologers', icon: VerifiedUserIcon },
      { label: 'KYC Verification', path: '/astrologers/kyc', icon: VerifiedUserIcon, permission: 'astrologers.kyc' },
      { label: 'Register Astrologer', path: '/astrologers/register', icon: PersonAddAlt1Icon, permission: 'astrologers.write' },
      { label: 'Invite Astrologer', path: '/astrologers/invite', icon: MailOutlineIcon, permission: 'astrologers.write' },
      { label: 'Inquiries', path: '/astrologer-inquiries', icon: MailOutlineIcon, permission: 'astrologers.read' },
    ],
  },
  { label: 'Chat Logs', icon: ChatIcon, path: '/chat-logs', permission: 'support.read' },
  { label: 'Call Logs', icon: LiveTvIcon, path: '/call-logs', permission: 'support.read' },
  {
    label: 'Wallet',
    icon: AccountBalanceWalletIcon,
    permission: 'finance.commissions',
    children: [
      { label: 'Transactions', path: '/transactions', icon: CoinsIcon },
      { label: 'Coin Management', path: '/wallet/coins', icon: CoinsIcon },
    ],
  },
  {
    label: 'Shop',
    icon: ShoppingBagIcon,
    permission: 'shop.read',
      children: [
        { label: 'Products', path: '/shop/products', icon: InventoryIcon },
        { label: 'Orders', path: '/shop/orders', icon: ReceiptIcon },
        { label: 'Coupons', path: '/shop/coupons', icon: ConfirmationNumberIcon },
        { label: 'Categories', path: '/categories', icon: CategoryIcon },
      ],
  },
  {
    label: 'Pooja',
    icon: SelfImprovementIcon,
    permission: 'shop.read',
    children: [
      { label: 'Manage Poojas', path: '/pooja-types', icon: SelfImprovementIcon },
      { label: 'Pooja Orders', path: '/pooja-orders', icon: ReceiptIcon },
    ],
  },
  { label: 'Blog', icon: ArticleIcon, path: '/blogs', permission: 'shop.read' },
  { label: 'Courses', icon: SchoolIcon, path: '/courses', permission: 'shop.read' },
  { label: 'Commissions', icon: CurrencyExchangeIcon, path: '/commissions', permission: 'finance.commissions' },
  {
    label: 'Reports',
    icon: AssessmentIcon,
    permission: 'reports.revenue',
    children: [
      { label: 'Revenue', path: '/reports/revenue' },
      { label: 'GST', path: '/reports/gst' },
      { label: 'TDS', path: '/reports/tds' },
    ],
  },
  { label: 'Payouts', icon: PaymentIcon, path: '/payouts', permission: 'finance.payouts' },
  { label: 'Live Streams', icon: LiveTvIcon, path: '/live-streams', permission: 'astrologers.read' },
  {
    label: 'Support',
    icon: SupportAgentIcon,
    permission: 'support.read',
    children: [
      { label: 'Tickets', path: '/support/tickets', icon: ConfirmationNumberIcon },
    ],
  },
  {
    label: 'Settings',
    icon: SettingsIcon,
    permission: 'settings.read',
    children: [
      { label: 'General', path: '/settings' },
      { label: 'Integrations', path: '/settings/integrations', icon: LanIcon },
      { label: 'Payment Gateways', path: '/settings/payment', icon: PaymentIcon },
      { label: 'Currency', path: '/settings/currency' },
      { label: 'Notifications', path: '/settings/notifications', icon: NotificationsIcon },
    ],
  },
];

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [openMenus, setOpenMenus] = useState({});
  const { hasPermission } = useContext(AuthContext);

  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.permission) return true;
    if (hasPermission(item.permission)) return true;
    if (item.children && item.children.some((c) => !c.permission || hasPermission(c.permission))) return true;
    return false;
  });

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getActiveParent = () => {
    for (const item of filteredMenuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (isActive(child.path)) return item.label;
        }
      } else if (isActive(item.path)) return item.label;
    }
    return null;
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Auto-open active parent
  const activeParent = getActiveParent();
  if (activeParent && !openMenus[activeParent]) {
    setOpenMenus((prev) => ({ ...prev, [activeParent]: true }));
  }

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AutoAwesomeIcon sx={{ color: '#FFD700', fontSize: 32 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {APP_NAME}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, overflowY: 'auto', py: 1, px: 1 }}>
        {filteredMenuItems.map((item) => {
          if (item.children) {
            const isOpen = openMenus[item.label];
            const hasActive = item.children.some((c) => isActive(c.path));
            return (
              <Box key={item.label}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleMenu(item.label)}
                    sx={{ borderRadius: 2, bgcolor: hasActive ? 'rgba(45,27,105,0.08)' : 'transparent' }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <item.icon sx={{ color: hasActive ? 'primary.main' : 'text.secondary', fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { fontWeight: hasActive ? 600 : 400, fontSize: '0.9rem' } }} />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => { navigate(child.path); if (!isDesktop) onClose(); }}
                          selected={isActive(child.path)}
                          sx={{ borderRadius: 2, ml: 2, pl: 2 }}
                        >
                          {child.icon && (
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <child.icon sx={{ fontSize: 18 }} />
                            </ListItemIcon>
                          )}
                          <ListItemText primary={child.label} sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); if (!isDesktop) onClose(); }}
                selected={isActive(item.path)}
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary', fontSize: 22 }} />
                </ListItemIcon>
                <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { fontWeight: isActive(item.path) ? 600 : 400, fontSize: '0.9rem' } }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={isDesktop ? true : open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default Sidebar;
